import {
  DynamoDBClient,
  QueryCommand,
  type AttributeValue,
  type WriteRequest,
} from "@aws-sdk/client-dynamodb";
import { unmarshall } from "@aws-sdk/util-dynamodb";
import { fetchAuthSession } from "aws-amplify/auth";
import { AwsBatchWriteQueue } from "@models/storage/AwsBatchWriteQueue";
import { registerAwsCredentialClientReset } from "@models/storage/awsCredentialClientRegistry";
import { loadCloudConfig, type CloudConfig } from "@models/storage/cloudConfig";
import { randomUuid } from "@models/utils/uuid";
import type {
  AITranscriptContext,
  AITranscriptInputType,
  AITranscriptRecallExchange,
  AITranscriptRecallRequest,
  AITranscriptRecallResult,
  AITranscriptRealtimeLogEvent,
  IAITranscriptService,
} from "./IAITranscriptService";

// Leave ample room below DynamoDB's 400 KB item limit for keys and metadata.
const K_MAX_CONTENT_BYTES = 300_000;
/** Keeps both selection lists comfortably inside DynamoDB's remaining item budget. */
const K_MAX_INTERACTION_IDS_PER_LIST = 500;
const K_MAX_INTERACTION_ID_BYTES_PER_LIST = 24_000;
const K_MAX_INTERACTION_NAME_BYTES = 24_000;
const K_USER_TURNS_INDEX = "gsiUserTurns";
const K_MAX_RECALL_READ_ITEMS = 5_000;
const K_RECALL_PAGE_SIZE = 250;
const K_SIMILAR_STOP_WORDS = new Set([
  "a", "about", "an", "and", "ask", "asked", "did", "do", "for", "from",
  "have", "i", "in", "is", "it", "me", "my", "of", "on", "or", "said",
  "say", "tell", "that", "the", "this", "to", "was", "what", "when", "you",
]);

interface PendingTurn {
  readonly exchangeId: string;
  readonly questionTurnId: string;
  readonly questionTimestamp: number;
  readonly context: AITranscriptContext;
}

/**
 * DynamoDB-backed transcript writer. Writes are observational and queued so a
 * transient cloud failure never delays the realtime conversation.
 */
export class AwsAITranscriptModel implements IAITranscriptService {
  private readonly m_queue: AwsBatchWriteQueue;
  private m_readClient: DynamoDBClient;
  private readonly m_tableName: string;
  private readonly m_config: CloudConfig;
  private readonly m_unregisterCredentialReset: () => void;
  private m_conversationId = randomUuid();
  private m_pendingTurn: PendingTurn | null = null;

  constructor(config: CloudConfig = loadCloudConfig()) {
    this.m_config = config;
    this.m_tableName = config.transcriptTableName;
    this.m_queue = new AwsBatchWriteQueue(this.m_tableName, config);
    this.m_readClient = this.createReadClient();
    this.m_unregisterCredentialReset = registerAwsCredentialClientReset(() => {
      this.m_readClient.destroy();
      this.m_readClient = this.createReadClient();
      this.resetConversation();
    });
  }

  private createReadClient(): DynamoDBClient {
    return new DynamoDBClient({
      region: this.m_config.region,
      credentials: async () => {
        const session = await fetchAuthSession();
        const credentials = session.credentials;
        if (!credentials?.accessKeyId || !credentials.secretAccessKey) {
          throw new Error("[AwsAITranscriptModel] No AWS credentials available.");
        }
        return {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
          expiration: credentials.expiration,
        };
      },
    });
  }

  recordUserMessage(
    content: string,
    inputType: AITranscriptInputType,
    context: AITranscriptContext,
  ): void {
    const text = content?.trim() ?? "";
    if (!text || !isValidContext(context)) return;

    const timestamp = Date.now();
    const exchangeId = randomUuid();
    const turnId = randomUuid();
    this.m_pendingTurn = {
      exchangeId,
      questionTurnId: turnId,
      questionTimestamp: timestamp,
      context,
    };
    this.m_queue.enqueue(
      put(messageItem({
        context,
        conversationId: this.m_conversationId,
        exchangeId,
        turnId,
        role: "user",
        content: text,
        inputType,
        timestamp,
      })),
    );
  }

  recordAssistantMessage(content: string): void {
    const text = content?.trim() ?? "";
    const pending = this.m_pendingTurn;
    if (!text || !pending) return;

    // Settle first so duplicate completion events cannot write the answer twice.
    this.m_pendingTurn = null;
    const timestamp = Date.now();
    this.m_queue.enqueue(
      put(messageItem({
        context: pending.context,
        conversationId: this.m_conversationId,
        exchangeId: pending.exchangeId,
        turnId: randomUuid(),
        role: "assistant",
        content: text,
        inputType: "text",
        timestamp,
        replyToTurnId: pending.questionTurnId,
        responseLatencyMs: Math.max(0, timestamp - pending.questionTimestamp),
      })),
    );
  }

  recordRealtimeLogEvent(
    args: AITranscriptRealtimeLogEvent,
    context: AITranscriptContext,
  ): void {
    if (!isValidContext(context) || !isValidRealtimeLogEvent(args)) return;
    this.m_queue.enqueue(put(realtimeLogItem({ context, event: args })));
  }

  async recall(request: AITranscriptRecallRequest): Promise<AITranscriptRecallResult> {
    if (
      !request.customerId
      || !request.userId
      || !Number.isFinite(request.sinceTimestamp)
      || !Number.isFinite(request.untilTimestamp)
      || request.sinceTimestamp > request.untilTimestamp
    ) {
      return { exchanges: [], readTruncated: false, omittedMatchCount: 0 };
    }

    const rawItems: Record<string, AttributeValue>[] = [];
    let lastKey: Record<string, AttributeValue> | undefined;
    let readTruncated = false;
    do {
      const remaining = K_MAX_RECALL_READ_ITEMS - rawItems.length;
      if (remaining <= 0) {
        readTruncated = lastKey !== undefined;
        break;
      }
      const response = await this.m_readClient.send(new QueryCommand({
        TableName: this.m_tableName,
        IndexName: K_USER_TURNS_INDEX,
        KeyConditionExpression: "gsi1pk = :pk AND gsi1sk BETWEEN :start AND :end",
        ExpressionAttributeValues: {
          ":pk": { S: `${request.customerId}:user:${request.userId}` },
          ":start": { S: recallSortKey(request.sinceTimestamp, "") },
          ":end": { S: recallSortKey(request.untilTimestamp, "\uffff") },
        },
        ScanIndexForward: false,
        Limit: Math.min(K_RECALL_PAGE_SIZE, remaining),
        ExclusiveStartKey: lastKey,
      }));
      if (response.Items) rawItems.push(...response.Items);
      lastKey = response.LastEvaluatedKey;
    } while (lastKey);

    if (rawItems.length >= K_MAX_RECALL_READ_ITEMS && lastKey) readTruncated = true;
    const matches = buildRecallExchanges(rawItems, request);
    const maxResults = Math.max(1, Math.floor(request.maxResults));
    return {
      exchanges: matches.slice(0, maxResults),
      readTruncated,
      omittedMatchCount: Math.max(0, matches.length - maxResults),
    };
  }

  resetConversation(): void {
    this.m_conversationId = randomUuid();
    this.m_pendingTurn = null;
  }

  flushPending(): Promise<void> {
    return this.m_queue.flushPending();
  }

  dispose(): Promise<void> {
    this.m_unregisterCredentialReset();
    return this.m_queue.dispose().finally(() => this.m_readClient.destroy());
  }
}

interface MessageItemArgs {
  readonly context: AITranscriptContext;
  readonly conversationId: string;
  readonly exchangeId: string;
  readonly turnId: string;
  readonly role: "user" | "assistant";
  readonly content: string;
  readonly inputType: AITranscriptInputType;
  readonly timestamp: number;
  readonly replyToTurnId?: string;
  readonly responseLatencyMs?: number;
}

interface RealtimeLogItemArgs {
  readonly context: AITranscriptContext;
  readonly event: AITranscriptRealtimeLogEvent;
}

function messageItem(args: MessageItemArgs): Record<string, AttributeValue> {
  const { context } = args;
  const content = fitContent(args.content);
  const item: Record<string, AttributeValue> = {
    // The base key supports account-wide time ranges. The sparse user index
    // supports user-scoped memory and report queries over the same turns.
    pk: { S: `${context.customerId}:turn` },
    sk: { S: `T#${args.timestamp}#U#${context.userId}#${args.turnId}` },
    gsi1pk: { S: `${context.customerId}:user:${context.userId}` },
    gsi1sk: { S: `T#${args.timestamp}#${args.turnId}` },
    entity_type: { S: "ai_transcript_message" },
    schema_version: { N: "3" },
    customer_id: { S: context.customerId },
    project_id: { S: context.projectId },
    project_customer_id: { S: context.projectCustomerId },
    conversation_id: { S: args.conversationId },
    exchange_id: { S: args.exchangeId },
    turn_id: { S: args.turnId },
    message_id: { S: args.turnId },
    role: { S: args.role },
    content: { S: content.text },
    input_type: { S: args.inputType },
    user_id: { S: context.userId },
    user_display_name: { S: context.userDisplayName },
    workflow_id: nullableString(context.workflowId),
    step_id: nullableString(context.stepId),
    step_name: nullableString(context.stepName),
    scene_id: nullableString(context.sceneId),
    agent_role_id: { S: context.agentRoleId },
    model_id: { S: context.modelId },
    realtime_session_id: { S: context.realtimeSessionId },
    device_type: { S: "Web" },
    creation_date: { N: String(args.timestamp) },
    created_at: { S: new Date(args.timestamp).toISOString() },
  };
  if (args.role === "user") {
    item.user_interaction_state = interactionStateValue(context.userInteractionState);
  }
  if (content.truncated) {
    item.content_truncated = { BOOL: true };
    item.content_original_bytes = { N: String(content.originalBytes) };
  }
  if (args.replyToTurnId) {
    item.reply_to_turn_id = { S: args.replyToTurnId };
  }
  if (args.responseLatencyMs !== undefined) {
    item.response_latency_ms = { N: String(args.responseLatencyMs) };
  }
  return item;
}

function realtimeLogItem(args: RealtimeLogItemArgs): Record<string, AttributeValue> {
  const { context, event } = args;
  const payload = event.payload ? fitContent(event.payload) : null;
  const item: Record<string, AttributeValue> = {
    pk: { S: `${context.customerId}:convolog` },
    sk: { S: `T#${event.timestamp}#U#${context.userId}` },
    entity_type: { S: "ai_realtime_log_event" },
    schema_version: { N: "1" },
    customer_id: { S: context.customerId },
    project_id: { S: context.projectId },
    project_customer_id: { S: context.projectCustomerId },
    user_id: { S: context.userId },
    user_display_name: { S: context.userDisplayName },
    workflow_id: nullableString(context.workflowId),
    step_id: nullableString(context.stepId),
    step_name: nullableString(context.stepName),
    scene_id: nullableString(context.sceneId),
    agent_role_id: { S: context.agentRoleId },
    model_id: { S: context.modelId },
    realtime_session_id: { S: context.realtimeSessionId },
    device_type: { S: "Web" },
    direction: { S: event.direction },
    event_type: { S: event.type },
    summary: nullableString(event.summary ?? null),
    payload: payload ? { S: payload.text } : { NULL: true },
    creation_date: { N: String(event.timestamp) },
    created_at: { S: new Date(event.timestamp).toISOString() },
  };
  if (payload?.truncated) {
    item.payload_truncated = { BOOL: true };
    item.payload_original_bytes = { N: String(payload.originalBytes) };
  }
  return item;
}

function nullableString(value: string | null): AttributeValue {
  return value ? { S: value } : { NULL: true };
}

function interactionStateValue(
  state: AITranscriptContext["userInteractionState"],
): AttributeValue {
  const selected = boundedIds(state.selectedSnapshotIds);
  const lastSelected = boundedIds(state.lastSelectedSnapshotIds);
  const names = boundedSnapshotNames(state.snapshotNames, [
    state.pointedSnapshotId,
    state.lastPointedSnapshotId,
    ...selected.values,
    ...lastSelected.values,
  ]);
  return {
    M: {
      selected_snapshot_ids: stringList(selected.values),
      selected_snapshot_ids_truncated: { BOOL: selected.truncated },
      last_selection_at: { N: String(state.lastSelectionAt) },
      last_selected_snapshot_ids: stringList(lastSelected.values),
      last_selected_snapshot_ids_truncated: { BOOL: lastSelected.truncated },
      pointed_snapshot_id: nullableString(state.pointedSnapshotId),
      last_pointed_at: { N: String(state.lastPointedAt) },
      last_pointed_snapshot_id: nullableString(state.lastPointedSnapshotId),
      snapshot_names: { M: names.values },
      snapshot_names_truncated: { BOOL: names.truncated },
    },
  };
}

function boundedIds(values: ReadonlyArray<string>): {
  readonly values: ReadonlyArray<string>;
  readonly truncated: boolean;
} {
  const bounded: string[] = [];
  const encoder = new TextEncoder();
  let bytes = 0;
  for (const value of values) {
    const valueBytes = encoder.encode(value).byteLength;
    if (
      bounded.length >= K_MAX_INTERACTION_IDS_PER_LIST
      || bytes + valueBytes > K_MAX_INTERACTION_ID_BYTES_PER_LIST
    ) {
      break;
    }
    bounded.push(value);
    bytes += valueBytes;
  }
  return {
    values: bounded,
    truncated: bounded.length < values.length,
  };
}

function stringList(values: ReadonlyArray<string>): AttributeValue {
  return { L: values.map((value) => ({ S: value })) };
}

function boundedSnapshotNames(
  names: Readonly<Record<string, string>>,
  referencedIds: ReadonlyArray<string | null>,
): {
  readonly values: Record<string, AttributeValue>;
  readonly truncated: boolean;
} {
  const entries: Array<[string, AttributeValue]> = [];
  const seen = new Set<string>();
  const encoder = new TextEncoder();
  let bytes = 0;
  let truncated = false;
  for (const id of referencedIds) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const name = names[id]?.trim();
    if (!name) continue;
    const entryBytes = encoder.encode(id).byteLength + encoder.encode(name).byteLength;
    if (bytes + entryBytes > K_MAX_INTERACTION_NAME_BYTES) {
      truncated = true;
      continue;
    }
    entries.push([id, { S: name }]);
    bytes += entryBytes;
  }
  return { values: Object.fromEntries(entries), truncated };
}

interface MutableRecallExchange {
  exchangeId: string;
  askedAt: number;
  answeredAt: number | null;
  question: string;
  answer: string | null;
  projectId: string;
  workflowId: string | null;
  stepId: string | null;
  stepName: string | null;
  sceneId: string | null;
  inputType: AITranscriptInputType;
}

function recallSortKey(timestamp: number, suffix: string): string {
  return `T#${Math.max(0, Math.floor(timestamp)).toString().padStart(13, "0")}#${suffix}`;
}

function buildRecallExchanges(
  rawItems: ReadonlyArray<Record<string, AttributeValue>>,
  request: AITranscriptRecallRequest,
): AITranscriptRecallExchange[] {
  const byExchange = new Map<string, MutableRecallExchange>();
  for (const raw of rawItems) {
    const row = unmarshall(raw) as Record<string, unknown>;
    const exchangeId = stringValue(row.exchange_id);
    const role = stringValue(row.role);
    const content = stringValue(row.content);
    const timestamp = numberValue(row.creation_date);
    if (!exchangeId || !content || timestamp === null || (role !== "user" && role !== "assistant")) {
      continue;
    }

    let exchange = byExchange.get(exchangeId);
    if (!exchange) {
      exchange = {
        exchangeId,
        askedAt: 0,
        answeredAt: null,
        question: "",
        answer: null,
        projectId: stringValue(row.project_id) ?? "",
        workflowId: stringValue(row.workflow_id),
        stepId: stringValue(row.step_id),
        stepName: stringValue(row.step_name),
        sceneId: stringValue(row.scene_id),
        inputType: stringValue(row.input_type) === "voice" ? "voice" : "text",
      };
      byExchange.set(exchangeId, exchange);
    }

    if (role === "user") {
      exchange.askedAt = timestamp;
      exchange.question = content;
      exchange.projectId = stringValue(row.project_id) ?? exchange.projectId;
      exchange.workflowId = stringValue(row.workflow_id);
      exchange.stepId = stringValue(row.step_id);
      exchange.stepName = stringValue(row.step_name);
      exchange.sceneId = stringValue(row.scene_id);
      exchange.inputType = stringValue(row.input_type) === "voice" ? "voice" : "text";
    } else {
      exchange.answeredAt = timestamp;
      exchange.answer = content;
    }
  }

  const query = normalizeSearchText(request.query ?? "");
  return [...byExchange.values()]
    .filter((exchange) => exchange.question.length > 0)
    .map((exchange) => ({
      exchange,
      score: recallMatchScore(exchange, query, request.matchType),
    }))
    .filter(({ score }) => query.length === 0 || score > 0)
    .sort((a, b) => b.score - a.score || b.exchange.askedAt - a.exchange.askedAt)
    .map(({ exchange }) => exchange);
}

function recallMatchScore(
  exchange: AITranscriptRecallExchange,
  normalizedQuery: string,
  matchType: "literal" | "similar",
): number {
  if (!normalizedQuery) return 1;
  const haystack = normalizeSearchText([
    exchange.question,
    exchange.answer ?? "",
    exchange.stepName ?? "",
  ].join("\n"));
  if (matchType === "literal") return haystack.includes(normalizedQuery) ? 1 : 0;

  const tokens = [...new Set(searchTokens(normalizedQuery))];
  if (tokens.length === 0) return haystack.includes(normalizedQuery) ? 1 : 0;
  const haystackTokens = new Set(searchTokens(haystack));
  const matched = tokens.filter((token) => haystackTokens.has(token)).length;
  if (matched === 0) return 0;
  return matched / tokens.length + (haystack.includes(normalizedQuery) ? 1 : 0);
}

function searchTokens(text: string): string[] {
  return (text.match(/[\p{L}\p{N}]+/gu) ?? [])
    .filter((token) => token.length > 1 && !K_SIMILAR_STOP_WORDS.has(token));
}

function normalizeSearchText(text: string): string {
  return text.normalize("NFKC").toLocaleLowerCase();
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function numberValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function fitContent(text: string): {
  readonly text: string;
  readonly truncated: boolean;
  readonly originalBytes: number;
} {
  const encoder = new TextEncoder();
  const originalBytes = encoder.encode(text).byteLength;
  if (originalBytes <= K_MAX_CONTENT_BYTES) {
    return { text, truncated: false, originalBytes };
  }

  const suffix = "\n… [transcript truncated]";
  const budget = K_MAX_CONTENT_BYTES - encoder.encode(suffix).byteLength;
  let low = 0;
  let high = text.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (encoder.encode(text.slice(0, mid)).byteLength <= budget) low = mid;
    else high = mid - 1;
  }
  return {
    text: `${text.slice(0, low)}${suffix}`,
    truncated: true,
    originalBytes,
  };
}

function isValidContext(context: AITranscriptContext): boolean {
  return Boolean(context.customerId && context.projectId && context.userId);
}

function isValidRealtimeLogEvent(args: AITranscriptRealtimeLogEvent): boolean {
  return Number.isFinite(args.timestamp) && args.timestamp >= 0 && Boolean(args.direction && args.type);
}

function put(item: Record<string, AttributeValue>): WriteRequest {
  return { PutRequest: { Item: item } };
}
